import React, { useRef, useEffect } from 'react';
import { Loader2, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import MessageBubble from './MessageBubble';
import AIGuardOutageNotice from '@/components/common/AIGuardOutageNotice';
import type { AIMessage } from '@/hooks/useAIAssistant';

interface Props {
  messages: AIMessage[];
  loading: boolean;
  isStreaming: boolean;
  streamingText: string;
  activeToolCalls: { name: string; args: Record<string, any> }[];
  onFeedback: (id: string, feedback: 'helpful' | 'not_helpful') => void;
  onRetryGuardOutage: (noticeId: string) => void;
}

export default function MessageList({ messages, loading, isStreaming, streamingText, activeToolCalls, onFeedback, onRetryGuardOutage }: Props) {
  const { t } = useTranslation();
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-[#9a9aac]" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {messages.map(msg => msg.guardOutage ? (
        <AIGuardOutageNotice
          key={msg.id}
          className="ltr:mr-8 rtl:ml-8"
          disabled={isStreaming}
          onRetry={() => onRetryGuardOutage(msg.id)}
        />
      ) : (
        <MessageBubble
          key={msg.id}
          id={msg.id}
          role={msg.role}
          content={msg.content}
          toolCalls={msg.toolCalls ? [msg.toolCalls as any] : undefined}
          feedback={msg.feedback}
          onFeedback={onFeedback}
        />
      ))}

      {/* Active tool calls */}
      {activeToolCalls.length > 0 && (
        <div className="space-y-1.5">
          {activeToolCalls.map((tc, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg p-2.5 bg-amber-500/6 border border-amber-500/20">
              <Loader2 size={12} className="animate-spin text-amber-400" />
              <Wrench size={12} className="text-amber-400" />
              <span className="text-xs text-amber-400">{t('aiAssistant.toolCalling')}: {tc.name.replace(/_/g, ' ')}</span>
            </div>
          ))}
        </div>
      )}

      {/* Streaming text */}
      {isStreaming && streamingText && (
        <div className="ltr:mr-8 rtl:ml-8 rounded-lg p-3 bg-white/2 border border-white/8">
          <p className="text-sm text-[#e0e0e8] whitespace-pre-wrap">{streamingText}</p>
          <div className="flex items-center gap-1 mt-2">
            <div className="w-1.5 h-1.5 rounded-full bg-iris-400 animate-pulse" />
          </div>
        </div>
      )}

      {/* Thinking indicator */}
      {isStreaming && !streamingText && activeToolCalls.length === 0 && (
        <div className="flex items-center gap-2 text-[#a0a0b0]">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-xs">{t('aiAssistant.thinking')}</span>
        </div>
      )}

      <div ref={endRef} />
    </div>
  );
}
